import type { JSTProctorApi } from "@indeed/tare/proctor/JSTProctor";

import { DatadogLogger } from "../../logging/DatadogLogger";
import { TareCodegenTst } from "../../proctor/__generated__/SharedProctorSpecifications";
import { applyProctorOverride } from "../../proctor/applyProctorOverride";
import { getProctorOverrides } from "../../proctor/proctorOverrideStore";

// The proctor bridge only exposes an async allocation API, so cache the most
// recent result for synchronous callers (e.g. getAccountStatusSync), defaulting
// to the legacy bridge until the async allocation resolves at least once.
let cachedIsEnabled = false;

// The resolved value can flip within a session (e.g. the server allocation lands
// after `getForcedWebBucketsString` overrides seed, or the async allocation
// resolves late). Once `isTareCodegenActive` has returned false at least once,
// the first subsequent true is treated as drift and logged a single time.
let hasReturnedFalse = false;
let hasLoggedDrift = false;

/**
 * Record each resolved activation value and emit a one-shot Datadog warning the
 * first time the flag returns true after having previously returned false.
 */
function logCodegenActivationDrift(isActive: boolean): void {
  if (!isActive) {
    hasReturnedFalse = true;
    return;
  }

  if (!hasReturnedFalse || hasLoggedDrift) {
    return;
  }
  // Mark optimistically so concurrent calls don't double-log, then reset if the
  // telemetry rejects so a later check can retry the one-shot warning.
  hasLoggedDrift = true;

  DatadogLogger.warn("tare.codegen.allocation_drift", {
    test: TareCodegenTst.name,
    driftDirection: "falseThenTrue",
  }).catch(() => {
    hasLoggedDrift = false;
  });
}

// Model getters read the gate on every access, so share one proctor lookup
// between concurrent callers and reuse a result for a short window.
const REFRESH_INTERVAL_MS = 1_000;
let inFlight: Promise<boolean> | null = null;
let lastResolvedAtMs = Number.NEGATIVE_INFINITY;

async function refreshTareCodegenActive(proctor: JSTProctorApi): Promise<boolean> {
  try {
    const allocation = await proctor.getProctorAllocation(TareCodegenTst);
    // Honor locally-forced buckets (debug web groups / prforceGroups) the same
    // way the generated `…ProctorProvider` does for React consumers.
    const baseAllocations = allocation ? { [TareCodegenTst.name]: allocation } : {};
    const merged = applyProctorOverride([TareCodegenTst], baseAllocations, getProctorOverrides());
    const value = merged[TareCodegenTst.name]?.value ?? TareCodegenTst.buckets.Inactive;
    cachedIsEnabled = value >= TareCodegenTst.buckets.Active;
  } catch {
    // Fail closed to the legacy bridge if the lookup rejects; callers never see
    // a rejection.
    cachedIsEnabled = false;
  }
  lastResolvedAtMs = Date.now();
  logCodegenActivationDrift(cachedIsEnabled);
  return cachedIsEnabled;
}

function isFresh(): boolean {
  return Date.now() - lastResolvedAtMs < REFRESH_INTERVAL_MS;
}

function startRefresh(proctor: JSTProctorApi): Promise<boolean> {
  inFlight ??= refreshTareCodegenActive(proctor).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/**
 * Resolve whether the TARE codegen migration is active for the current viewer,
 * refreshing the synchronous cache as a side effect.
 *
 * `Active` reads the TARE Codegen modules on `NativeModules` (`NativeJSTAccountStatus`,
 * `NativeJSTAppModel`, `NativeJSTAppSessionInfo`, `NativeJSTProctor`); every other bucket reads the
 * legacy codegen module the method originally lived on (`RNTareBridge`).
 */
export async function isTareCodegenActive(proctor: JSTProctorApi): Promise<boolean> {
  if (!inFlight && isFresh()) return cachedIsEnabled;
  return startRefresh(proctor);
}

/**
 * Best-effort synchronous read of the codegen migration flag: returns the last
 * cached value (defaulting to `false`/legacy bridge) and kicks off a refresh
 * when the cached value is stale.
 */
export function isTareCodegenActiveSync(proctor: JSTProctorApi): boolean {
  if (!inFlight && !isFresh()) void startRefresh(proctor);
  return cachedIsEnabled;
}

/** Test-only: forget the cached gate so each test starts from the legacy default. */
export function __resetTareCodegenActiveForTests(): void {
  cachedIsEnabled = false;
  hasReturnedFalse = false;
  hasLoggedDrift = false;
  inFlight = null;
  lastResolvedAtMs = Number.NEGATIVE_INFINITY;
}
